import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { uiLocale } from '@/lib/i18n';

export interface HelpCategory {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  order: number;
}

export interface HelpArticle {
  id: string;
  category: string;
  category_slug: string;
  category_title: string;
  subcategory: string | null;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  search_keywords: string[];
  views_count: number;
  helpful_count: number;
  target_audience: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface HelpSearchResult {
  id: string;
  category_slug: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content_snippet: string;
  relevance: number;
}

type Translate = (key: string, fallback: string) => string;

export const helpCategoriesQueryKey = (locale?: string) =>
  ['help', 'categories', locale ?? 'current'] as const;
export const helpArticlesQueryKey = (category?: string | null, locale?: string) =>
  ['help', 'articles', category ?? 'all', locale ?? 'current'] as const;
export const helpArticleQueryKey = (slug: string | null | undefined, locale?: string) =>
  ['help', 'article', slug, locale ?? 'current'] as const;
export const helpSearchQueryKey = (query: string, locale?: string) =>
  ['help', 'search', query, locale ?? 'current'] as const;
export const helpPopularQueryKey = (locale?: string) =>
  ['help', 'popular', locale ?? 'current'] as const;
export const helpRecentQueryKey = (locale?: string) =>
  ['help', 'recent', locale ?? 'current'] as const;

const categoryIcons: Record<string, string> = {
  'getting-started': 'book-open',
  'kom-godt-i-gang': 'book-open',
  account: 'users',
  billing: 'credit-card',
  fakturering: 'credit-card',
  features: 'bar-chart',
  troubleshooting: 'help-circle',
  integrations: 'settings',
  security: 'shield',
  api: 'file-text',
  patienter: 'users',
  kalender: 'calendar',
  spoergeskemaer: 'file-text',
  sms: 'message-square',
  indstillinger: 'settings',
  patientportal: 'users',
};

function formatCategoryTitle(category: string): string {
  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function translatedCategoryTitle(category: string, t: Translate): string {
  return t(`help.categories.${category}`, formatCategoryTitle(category));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRowToArticle(row: any, t: Translate): HelpArticle {
  return {
    id: row.id,
    category: row.category,
    category_slug: row.category,
    category_title: translatedCategoryTitle(row.category, t),
    subcategory: row.subcategory ?? null,
    slug: row.slug,
    title: row.title,
    excerpt: row.excerpt ?? null,
    content: row.content,
    search_keywords: row.search_keywords ?? [],
    views_count: row.views_count ?? 0,
    helpful_count: row.helpful_count ?? 0,
    target_audience: row.target_audience ?? null,
    order_index: row.order_index ?? 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

async function fetchPublishedByLocale(locale: 'da' | 'en') {
  const { data, error } = await supabase
    .from('help_articles')
    .select('*')
    .eq('is_published', true)
    .eq('locale', locale)
    .order('order_index', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/** Danish UI gets Danish articles. English UI prefers English copies, then fills gaps from Danish. */
async function fetchPublishedForUi() {
  const locale = uiLocale();
  if (locale === 'da') {
    return fetchPublishedByLocale('da');
  }
  const [english, danish] = await Promise.all([
    fetchPublishedByLocale('en'),
    fetchPublishedByLocale('da'),
  ]);
  if (english.length === 0) return danish;
  const englishSlugs = new Set(english.map((row) => row.slug));
  return [...english, ...danish.filter((row) => !englishSlugs.has(row.slug))];
}

export function useHelpCategories() {
  const { t, i18n } = useTranslation();
  const locale = uiLocale();

  return useQuery({
    queryKey: helpCategoriesQueryKey(`${locale}:${i18n.language}`),
    queryFn: async (): Promise<HelpCategory[]> => {
      const rows = await fetchPublishedForUi();
      const uniqueCategories = [...new Set(rows.map((row) => row.category))];

      return uniqueCategories.map((category, index) => ({
        id: category,
        slug: category,
        title: translatedCategoryTitle(category, t),
        description: '',
        icon: categoryIcons[category] ?? 'help-circle',
        order: index,
      }));
    },
    staleTime: 300_000,
  });
}

export function useHelpArticles(category?: string | null) {
  const { t, i18n } = useTranslation();
  const locale = uiLocale();

  return useQuery({
    queryKey: helpArticlesQueryKey(category, `${locale}:${i18n.language}`),
    queryFn: async (): Promise<HelpArticle[]> => {
      let rows = await fetchPublishedForUi();
      if (category) {
        rows = rows.filter((row) => row.category === category);
      }
      return rows.map((row) => mapRowToArticle(row, t));
    },
    staleTime: 300_000,
  });
}

export function useHelpArticle(categorySlug: string | undefined, articleSlug: string | undefined) {
  const { t, i18n } = useTranslation();
  const locale = uiLocale();

  return useQuery({
    queryKey: helpArticleQueryKey(articleSlug, `${locale}:${i18n.language}`),
    enabled: Boolean(categorySlug && articleSlug),
    queryFn: async (): Promise<HelpArticle | null> => {
      if (!categorySlug || !articleSlug) return null;

      const load = async (loc: 'da' | 'en') => {
        const { data, error } = await supabase
          .from('help_articles')
          .select('*')
          .eq('slug', articleSlug)
          .eq('category', categorySlug)
          .eq('locale', loc)
          .eq('is_published', true)
          .maybeSingle();
        if (error) throw error;
        return data;
      };

      const primary = await load(locale);
      const row = primary ?? (locale === 'en' ? await load('da') : null);
      return row ? mapRowToArticle(row, t) : null;
    },
    staleTime: 300_000,
  });
}

export function useHelpSearch(query: string) {
  const trimmedQuery = query.trim();
  const locale = uiLocale();

  return useQuery({
    queryKey: helpSearchQueryKey(trimmedQuery, locale),
    enabled: trimmedQuery.length >= 2,
    queryFn: async (): Promise<HelpSearchResult[]> => {
      if (trimmedQuery.length < 2) return [];

      const pattern = `%${trimmedQuery}%`;
      const search = async (loc: 'da' | 'en') => {
        const { data, error } = await supabase
          .from('help_articles')
          .select('id, slug, title, excerpt, content, category')
          .eq('is_published', true)
          .eq('locale', loc)
          .or(`title.ilike.${pattern},excerpt.ilike.${pattern},content.ilike.${pattern}`)
          .limit(10);
        if (error) throw error;
        return data ?? [];
      };

      let rows = await search(locale);
      if (rows.length === 0 && locale === 'en') {
        rows = await search('da');
      }

      return rows.map((row, idx) => ({
        id: row.id,
        category_slug: row.category,
        slug: row.slug,
        title: row.title,
        excerpt: row.excerpt ?? null,
        content_snippet: row.content ? row.content.substring(0, 150) + '...' : '',
        relevance: 1 - idx * 0.05,
      }));
    },
    staleTime: 60_000,
  });
}

export function useHelpPopular(limit: number = 5) {
  const { t, i18n } = useTranslation();
  const locale = uiLocale();

  return useQuery({
    queryKey: helpPopularQueryKey(`${locale}:${i18n.language}`),
    queryFn: async (): Promise<HelpArticle[]> => {
      const rows = await fetchPublishedForUi();
      return rows
        .slice()
        .sort((a, b) => (b.views_count ?? 0) - (a.views_count ?? 0))
        .slice(0, limit)
        .map((row) => mapRowToArticle(row, t));
    },
    staleTime: 300_000,
  });
}

export function useHelpRecent(limit: number = 3) {
  const { t, i18n } = useTranslation();
  const locale = uiLocale();

  return useQuery({
    queryKey: helpRecentQueryKey(`${locale}:${i18n.language}`),
    queryFn: async (): Promise<HelpArticle[]> => {
      const recentStr = localStorage.getItem('help_recent_articles');
      if (!recentStr) return [];

      const recentIds: string[] = JSON.parse(recentStr);
      if (recentIds.length === 0) return [];

      const { data, error } = await supabase
        .from('help_articles')
        .select('*')
        .in('id', recentIds.slice(0, limit))
        .eq('is_published', true);

      if (error) throw error;

      const sorted = (data ?? []).sort((a, b) => {
        return recentIds.indexOf(a.id) - recentIds.indexOf(b.id);
      });

      return sorted.map((row) => mapRowToArticle(row, t));
    },
    staleTime: 60_000,
  });
}

export function trackHelpArticleView(articleId: string) {
  const recentStr = localStorage.getItem('help_recent_articles');
  const recent: string[] = recentStr ? JSON.parse(recentStr) : [];
  const filtered = recent.filter((id) => id !== articleId);
  const updated = [articleId, ...filtered].slice(0, 10);
  localStorage.setItem('help_recent_articles', JSON.stringify(updated));
}
